import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowSquareOut, CaretDown, CaretRight, CheckCircle, ChatCircleText,
  Clock, Download, FileText, Globe, Lightbulb, Lightning, Lock, MagnifyingGlass,
  Rocket, Scales, ShieldCheck, Sparkle, Target, Warning, XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import ScanningStage from "@/components/ScanningStage";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { fmtDate, scoreClass, scoreLabel, shortUrl } from "@/lib/score-utils";

// Section accent palette — matches PDF report colors exactly so the
// in-app report and downloaded PDF feel like the same document.
const SECTION_THEME = {
  seo:           { color: "#1D4ED8", soft: "rgba(29,78,216,0.08)",  label: "SEO",            blurb: "Discoverability signals" },
  geo:           { color: "#0891B2", soft: "rgba(8,145,178,0.08)",  label: "GEO",            blurb: "Generative Engine Optimisation" },
  aeo:           { color: "#9333EA", soft: "rgba(147,51,234,0.08)", label: "AEO",            blurb: "Answer Engine Optimisation" },
  performance:   { color: "#0F766E", soft: "rgba(15,118,110,0.08)", label: "Performance",    blurb: "Speed & payload economics" },
  security:      { color: "#B91C1C", soft: "rgba(185,28,28,0.08)",  label: "Security",       blurb: "Transport & header hardening" },
  accessibility: { color: "#6D28D9", soft: "rgba(109,40,217,0.08)", label: "Accessibility",  blurb: "Inclusive markup & semantics" },
  tech:          { color: "#334155", soft: "rgba(51,65,85,0.08)",   label: "Tech Stack",     blurb: "BuiltWith-style fingerprint" },
};

const SECTIONS = [
  { key: "seo",           icon: MagnifyingGlass },
  { key: "geo",           icon: Sparkle, upsell: true },
  { key: "aeo",           icon: ChatCircleText, upsell: true },
  { key: "performance",   icon: Lightning },
  { key: "security",      icon: ShieldCheck },
  { key: "accessibility", icon: CheckCircle },
];

// Yellow Thinking enquiry endpoint. Pre-fills issue, source URL, and a
// utm_source so YT can attribute leads back to the Auditor product.
function ytEnquiryUrl({ siteUrl, sectionLabel, issue, advice }) {
  const params = new URLSearchParams({
    ref: "auditor",
    utm_source: "auditor",
    utm_medium: "audit-report",
    utm_campaign: sectionLabel.toLowerCase(),
    service: sectionLabel === "GEO" ? "ai-search-optimisation" : "answer-engine-optimisation",
    site: siteUrl || "",
    issue: issue || "",
    detail: advice || "",
  });
  return `https://www.yellowthinking.com/contact?${params.toString()}`;
}

// SVG donut for scores. Renders an arc proportional to value/100 in the
// supplied accent color, with the score number centered in display font.
function Donut({ value = 0, size = 96, stroke = 8, color = "#0A0A0A", trackColor = "rgba(0,0,0,0.08)", showLabel = true, labelClassName = "" }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const safe = Math.max(0, Math.min(100, value ?? 0));
  const dash = (safe / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
          style={{ transition: "stroke-dasharray 0.8s cubic-bezier(.22,.61,.36,1)" }}
        />
      </svg>
      {showLabel && (
        <div className={`absolute inset-0 flex items-center justify-center num-display ${labelClassName}`} style={{ color }}>
          {value ?? "—"}
        </div>
      )}
    </div>
  );
}

export default function AuditDetailPage() {
  const { id } = useParams();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const { billing } = useAuth();
  const canExport = !!billing?.features?.pdf;

  const load = async () => {
    try {
      const { data } = await api.get(`/audits/${id}`);
      setAudit(data);
      if (data.status !== "complete" && data.status !== "failed") {
        return false;
      }
      return true;
    } catch (err) {
      toast.error("Failed to load audit");
      nav("/app/dashboard");
      return true;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    let timer;
    const poll = async () => {
      if (!alive) return;
      const done = await load();
      if (!done && alive) {
        timer = setTimeout(poll, 2500);
      }
    };
    poll();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [id]);

  const download = async (kind) => {
    try {
      const token = localStorage.getItem("audit_token");
      const res = await fetch(`${API}/audits/${id}/export/${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 402) {
        toast.error("Export requires Pro or Agency plan");
        nav("/app/billing");
        return;
      }
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `audit-${id}.${kind === "pdf" ? "pdf" : "json"}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  if (loading && !audit) {
    return (
      <AppShell>
        <div className="text-ink-muted" data-testid="audit-loading">Loading audit…</div>
      </AppShell>
    );
  }
  if (!audit) return null;

  if (audit.status !== "complete") {
    return <ScanningStage audit={audit} />;
  }

  const r = audit.result;
  const adminView = audit.admin_view;
  return (
    <AppShell>
      <Link to="/app/dashboard" data-testid="audit-back-link"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink mb-4 transition-colors">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>

      {adminView && (
        <div
          className="border border-critical rounded-sm px-5 py-3 mb-6 flex items-center gap-3 hard-shadow-sm"
          style={{ background: "rgba(185,28,28,0.08)" }}
          data-testid="admin-view-banner"
        >
          <Warning size={20} weight="fill" className="text-critical shrink-0" />
          <div className="flex-1 text-sm">
            <span className="font-bold text-critical">Viewing as admin · </span>
            this audit belongs to{" "}
            <span className="font-semibold">{adminView.owner_name}</span>{" "}
            <span className="mono text-xs text-ink-muted">({adminView.owner_email})</span>
          </div>
          <Link to="/app/admin/users" data-testid="admin-view-banner-back"
            className="text-xs mono uppercase tracking-wider font-bold text-critical hover:underline">
            Back to users
          </Link>
        </div>
      )}

      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div className="min-w-0">
          <div className="label-overline mb-2">Audit report</div>
          <h1 className="font-display text-3xl lg:text-4xl font-black tracking-tight leading-tight break-all" data-testid="audit-url-heading">
            {shortUrl(r.url)}
          </h1>
          <div className="flex items-center gap-4 mt-2 text-xs text-ink-muted mono">
            <span>{fmtDate(audit.completed_at)}</span>
            <span>·</span>
            <span>{r.duration_ms} ms</span>
            <span>·</span>
            <a href={r.final_url} target="_blank" rel="noreferrer" className="hover:text-brand inline-flex items-center gap-1">
              Open <ArrowSquareOut size={12} />
            </a>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {r?.status === "complete" && (
            <Link
              to={`/app/compare?add=${r.id}`}
              data-testid="add-to-compare-button"
              className="inline-flex items-center gap-2 border border-brand text-brand px-4 py-2.5 rounded-sm font-semibold text-sm hover:bg-brand hover:text-white transition-colors"
              title="Stack this audit against others side-by-side"
            >
              <Scales size={16} /> Compare
            </Link>
          )}
          {canExport ? (
            <>
              <button
                onClick={() => download("pdf")}
                data-testid="export-pdf-button"
                className="inline-flex items-center gap-2 border border-ink px-4 py-2.5 rounded-sm font-semibold text-sm hover:bg-ink hover:text-white transition-colors"
              >
                <FileText size={16} /> PDF
              </button>
              <button
                onClick={() => download("json")}
                data-testid="export-json-button"
                className="inline-flex items-center gap-2 border border-ink px-4 py-2.5 rounded-sm font-semibold text-sm hover:bg-ink hover:text-white transition-colors"
              >
                <Download size={16} /> JSON
              </button>
            </>
          ) : (
            <Link to="/app/billing" data-testid="upgrade-to-export"
              className="inline-flex items-center gap-2 border border-warning text-warning px-4 py-2.5 rounded-sm font-semibold text-sm hover:bg-warning hover:text-white transition-colors">
              <Lock size={16} /> Upgrade to export
            </Link>
          )}
        </div>
      </div>

      {/* HERO score panel — dark cinematic background with big overall donut + 6 sub-donuts */}
      <div
        className="relative overflow-hidden rounded-sm border border-ink mb-10 text-white hard-shadow-sm"
        style={{
          background:
            "radial-gradient(ellipse 50% 70% at 0% 50%, rgba(255,255,255,0.06), transparent 60%)," +
            "linear-gradient(135deg, #0A0A0C 0%, #11141A 50%, #050507 100%)",
        }}
        data-testid="score-hero"
      >
        {/* Decorative grid texture */}
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.9) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.9) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        {/* Faint scan beam */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent" />

        <div className="relative grid lg:grid-cols-[1.1fr_2.4fr] gap-8 lg:gap-12 p-6 lg:p-10">
          {/* OVERALL — big donut + tagline */}
          <div className="flex items-center gap-6 lg:border-r lg:border-white/10 lg:pr-8">
            <Donut
              value={r.scores.overall}
              size={148}
              stroke={11}
              color={scoreColorHex(r.scores.overall)}
              trackColor="rgba(255,255,255,0.10)"
              labelClassName="text-5xl font-black"
            />
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.22em] font-semibold text-white/50 uppercase mb-1">Overall</div>
              <div className="font-display text-2xl font-black leading-tight">
                {scoreLabel(r.scores.overall)}
              </div>
              <div className="text-xs text-white/50 mono mt-1">
                {r.duration_ms} ms · {SECTIONS.length} categories
              </div>
            </div>
          </div>

          {/* 6 sub-donuts, color-coded per section */}
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-2">
            {SECTIONS.map(({ key, icon: Icon }) => {
              const t = SECTION_THEME[key];
              const v = r.scores[key];
              return (
                <div key={key} className="flex flex-col items-center gap-2 group" data-testid={`score-${key}`}>
                  <Donut
                    value={v}
                    size={72}
                    stroke={7}
                    color={t.color}
                    trackColor="rgba(255,255,255,0.10)"
                    labelClassName="text-xl font-black"
                  />
                  <div className="flex items-center gap-1.5">
                    <Icon size={11} weight="fill" style={{ color: t.color }} />
                    <div className="text-[9px] tracking-[0.18em] font-semibold text-white/70 uppercase">{t.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* AI summary — re-rendered as a designed, parsed block */}
      {r.ai_summary ? (
        <AIRecommendations summary={r.ai_summary} />
      ) : (
        billing && !billing.features?.ai && (
          <div className="surface-card border-dashed p-6 lg:p-8 mb-10 bg-secondary" data-testid="ai-locked-card">
            <div className="flex items-start gap-3">
              <Lock size={22} className="text-warning shrink-0 mt-1" weight="duotone" />
              <div>
                <div className="label-overline mb-1">Premium feature</div>
                <div className="font-display font-bold text-xl mb-2">Prioritised recommendations are gated on the {billing.plan_label} plan</div>
                <p className="text-sm text-ink-muted mb-4 max-w-xl">
                  Upgrade to Pro and every audit comes with a prioritised
                  fix-list — each issue explained in plain language with a
                  concrete action you can take today.
                </p>
                <Link to="/app/billing" data-testid="ai-locked-upgrade"
                  className="inline-flex items-center gap-2 bg-ink text-white px-4 py-2 text-sm rounded-sm font-semibold hover:bg-brand transition-colors">
                  See plans <ArrowSquareOut size={14} />
                </Link>
              </div>
            </div>
          </div>
        )
      )}

      {/* Section blocks */}
      <div className="space-y-8">
        {SECTIONS.map(({ key, icon: Icon, upsell }) => (
          <SectionBlock
            key={key}
            icon={Icon}
            sectionKey={key}
            section={r[key]}
            testId={`section-${key}`}
            upsell={upsell ? { sectionLabel: SECTION_THEME[key].label, siteUrl: r.url } : null}
          />
        ))}
        <TechStack tech={r.tech_stack} />
      </div>
    </AppShell>
  );
}

function ScoreTile({ label, value, big, testId }) {
  return (
    <div className="bg-white p-5 lg:p-6" data-testid={testId}>
      <div className="label-overline mb-2">{label}</div>
      <div className={`num-display ${big ? "text-6xl lg:text-7xl" : "text-4xl"} ${scoreClass(value)} leading-none`}>
        {value ?? "—"}
      </div>
      <div className="text-xs text-ink-muted mt-2 mono">{scoreLabel(value)}</div>
    </div>
  );
}

// Return a hex colour matching the score band (used inside SVG donut)
function scoreColorHex(v) {
  if (v == null) return "#9CA3AF";
  if (v >= 80) return "#16A34A"; // success
  if (v >= 50) return "#EA580C"; // warning
  return "#DC2626";              // critical
}

// Parse the AI summary into 4 structured sections we can render with their
// own visual treatment. The model is asked to emit:
//   1. Executive Summary
//   2. Top 5 Priority Fixes
//   3. Quick Wins
//   4. Long-term Improvements
// We split on the numbered/labelled headings and best-effort parse the body.
function parseAISummary(raw) {
  if (!raw) return null;
  const text = raw.replace(/\r/g, "").trim();
  const re = /(?:^|\n)\s*(?:\*\*)?\s*(?:[1-4][.):]\s*)?(Executive Summary|Top\s*5\s*Priority\s*Fixes|Quick Wins|Long[-\s]?term Improvements)(?:\*\*)?\s*[:\n]/gi;
  const headings = [];
  let m;
  while ((m = re.exec(text)) !== null) {
    headings.push({ name: m[1].toLowerCase(), start: m.index, headEnd: re.lastIndex });
  }
  if (headings.length === 0) return { raw: text };
  const sections = {};
  headings.forEach((h, i) => {
    const end = headings[i + 1] ? headings[i + 1].start : text.length;
    sections[h.name.replace(/\s+/g, " ")] = text.slice(h.headEnd, end).trim();
  });
  return {
    summary: findSection(sections, /executive summary/),
    fixes:   findSection(sections, /priority fixes/),
    wins:    findSection(sections, /quick wins/),
    longterm: findSection(sections, /long.?term/),
  };
}

function findSection(map, regex) {
  const key = Object.keys(map).find((k) => regex.test(k));
  return key ? map[key] : null;
}

function stripMd(s) {
  return (s || "").replace(/\*\*/g, "").replace(/`([^`]+)`/g, "$1").trim();
}

// Parse the top-5 fixes block into structured items so we can render
// numbered cards instead of a flat text wall.
function parsePriorityFixes(text) {
  if (!text) return [];
  const items = text.split(/\n(?=\s*[1-9][0-9]?\.\s)/).map((s) => s.trim()).filter(Boolean);
  return items.slice(0, 5).map((block) => {
    const titleMatch = block.match(/^[1-9][0-9]?\.\s*(.+?)(?:\n|$)/);
    const title = stripMd(titleMatch ? titleMatch[1] : block.split("\n")[0]);
    const rest = block.replace(/^[1-9][0-9]?\.\s*.+?(?:\n|$)/, "");
    const why = (rest.match(/\*?why[:*]\s*(.+?)(?=\n|\*?action[:*]|$)/is) || [])[1];
    const action = (rest.match(/\*?action[:*]\s*(.+?)$/is) || [])[1];
    return { title, why: stripMd(why), action: stripMd(action), raw: stripMd(rest) };
  });
}

// Parse bullet block (Quick Wins, Long-term)
function parseBullets(text) {
  if (!text) return [];
  return text
    .split(/\n/)
    .map((s) => s.replace(/^[\s•\-\*]+/, "").trim())
    .filter(Boolean)
    .map(stripMd);
}

function AIRecommendations({ summary }) {
  const parsed = useMemo(() => parseAISummary(summary), [summary]);
  if (!parsed) return null;

  // Fallback if we couldn't parse — render raw text but still styled
  if (parsed.raw) {
    return (
      <div className="surface-card hard-shadow-sm p-6 lg:p-8 mb-10" data-testid="ai-summary-card">
        <div className="flex items-center gap-2 mb-3">
          <Sparkle size={20} className="text-brand" weight="fill" />
          <div className="label-overline">Recommendations</div>
        </div>
        <pre className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-ink">
          {stripMd(parsed.raw)}
        </pre>
      </div>
    );
  }

  const fixes = parsePriorityFixes(parsed.fixes);
  const wins = parseBullets(parsed.wins);
  const longterm = parseBullets(parsed.longterm);

  return (
    <div className="mb-10" data-testid="ai-summary-card">
      {/* Executive summary — bold display, with sparkle icon */}
      {parsed.summary && (
        <div
          className="relative overflow-hidden rounded-sm border border-ink p-6 lg:p-8 mb-5 bg-white hard-shadow-sm"
          data-testid="ai-summary-exec"
        >
          <div
            className="absolute top-0 left-0 right-0 h-1"
            style={{ background: "linear-gradient(90deg, #1D4ED8, #0891B2, #9333EA, #0F766E)" }}
          />
          <div className="flex items-center gap-2 mb-4">
            <Sparkle size={18} className="text-brand" weight="fill" />
            <div className="label-overline">Executive Summary</div>
          </div>
          <p className="font-display text-xl lg:text-2xl font-medium leading-snug text-ink tracking-tight">
            {stripMd(parsed.summary)}
          </p>
        </div>
      )}

      {/* Top 5 priority fixes — numbered cards in 2-col grid */}
      {fixes.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3 px-1">
            <Target size={16} className="text-critical" weight="fill" />
            <div className="label-overline">Top {fixes.length} Priority Fixes</div>
          </div>
          <div className="grid md:grid-cols-2 gap-3" data-testid="ai-priority-fixes">
            {fixes.map((f, i) => (
              <div
                key={i}
                className="relative bg-white border border-border rounded-sm p-5 hover:border-ink transition-colors"
                data-testid={`ai-fix-${i + 1}`}
              >
                <div
                  className="absolute top-3 right-3 font-display num-display text-4xl leading-none"
                  style={{ color: "rgba(0,0,0,0.08)" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="pr-12">
                  <div className="font-display font-bold text-base mb-2 leading-tight">{f.title}</div>
                  {f.why && (
                    <div className="text-xs text-ink-muted mb-2">
                      <span className="font-semibold text-ink mono uppercase tracking-wider text-[10px] mr-1">Why</span>
                      {f.why}
                    </div>
                  )}
                  {f.action && (
                    <div className="text-xs">
                      <span className="font-semibold text-brand mono uppercase tracking-wider text-[10px] mr-1">Action</span>
                      <span className="text-ink">{f.action}</span>
                    </div>
                  )}
                  {!f.why && !f.action && f.raw && (
                    <div className="text-xs text-ink-muted">{f.raw}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Wins + Long-term — 2 cards side-by-side */}
      <div className="grid md:grid-cols-2 gap-3">
        {wins.length > 0 && (
          <div className="bg-white border border-border rounded-sm p-5" data-testid="ai-quick-wins">
            <div className="flex items-center gap-2 mb-3">
              <Lightning size={16} weight="fill" className="text-success" />
              <div className="label-overline">Quick Wins — under 15 min</div>
            </div>
            <ul className="space-y-2">
              {wins.map((w, i) => (
                <li key={i} className="text-sm text-ink flex gap-2">
                  <CheckCircle size={14} weight="fill" className="text-success mt-0.5 shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {longterm.length > 0 && (
          <div className="bg-white border border-border rounded-sm p-5" data-testid="ai-long-term">
            <div className="flex items-center gap-2 mb-3">
              <Rocket size={16} weight="fill" className="text-brand" />
              <div className="label-overline">Long-term Improvements</div>
            </div>
            <ul className="space-y-2">
              {longterm.map((l, i) => (
                <li key={i} className="text-sm text-ink flex gap-2">
                  <Clock size={14} weight="fill" className="text-brand mt-0.5 shrink-0" />
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionBlock({ sectionKey, icon: Icon, section, testId, upsell }) {
  const [open, setOpen] = useState(true);
  if (!section) return null;
  const theme = SECTION_THEME[sectionKey] || SECTION_THEME.tech;
  const checks = section.checks || [];
  const fails = checks.filter((c) => c.status !== "pass").length;
  const passes = checks.length - fails;

  return (
    <div
      className="surface-card overflow-hidden relative"
      data-testid={testId}
      style={{ boxShadow: "0 1px 0 0 rgba(0,0,0,0.04)" }}
    >
      {/* Left accent bar in section color */}
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: theme.color }} />

      <button
        onClick={() => setOpen(!open)}
        className="w-full grid grid-cols-[1fr_auto] items-center px-6 py-5 transition-colors text-left"
        style={{ background: open ? theme.soft : "white" }}
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-4 min-w-0">
          {/* Icon badge in section color */}
          <div
            className="w-12 h-12 rounded-sm flex items-center justify-center shrink-0"
            style={{ background: theme.color }}
          >
            <Icon size={24} weight="fill" className="text-white" />
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <div className="font-display font-black text-xl tracking-tight" style={{ color: theme.color }}>
                {theme.label}
              </div>
              <div className="text-xs text-ink-muted">— {theme.blurb}</div>
            </div>
            <div className="text-xs text-ink-muted mono mt-1 flex items-center gap-3">
              <span className="inline-flex items-center gap-1">
                <CheckCircle size={11} weight="fill" className="text-success" /> {passes} pass
              </span>
              <span className="inline-flex items-center gap-1">
                <Warning size={11} weight="fill" className="text-warning" /> {fails} need attention
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <Donut value={section.score} size={56} stroke={6} color={theme.color} labelClassName="text-base font-black" />
          {open ? <CaretDown size={16} className="text-ink-muted" /> : <CaretRight size={16} className="text-ink-muted" />}
        </div>
      </button>

      {open && (
        <>
          {upsell && fails > 0 && (
            <div
              className="border-t border-border px-6 py-3 flex items-center justify-between gap-4"
              style={{ background: theme.soft }}
              data-testid={`${testId}-upsell-banner`}
            >
              <div className="text-xs text-ink">
                <span className="font-semibold">Yellow Thinking</span> can fix every {theme.label} gap below — schema, copy, crawler rules, the lot.
              </div>
              <a
                href={ytEnquiryUrl({
                  siteUrl: upsell.siteUrl,
                  sectionLabel: upsell.sectionLabel,
                  issue: `Improve ${theme.label} — ${fails} gaps`,
                  advice: checks.filter((c) => c.status !== "pass").map((c) => `• ${c.label}`).join("\n"),
                })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 px-3 py-1.5 rounded-sm whitespace-nowrap"
                style={{ background: theme.color }}
                data-testid={`${testId}-fix-all-btn`}
              >
                Fix all {fails} {theme.label} issues
                <ArrowSquareOut size={12} weight="bold" />
              </a>
            </div>
          )}
          <div className="border-t border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary border-b border-border label-overline">
                <tr className="text-left">
                  <th className="px-6 py-2.5 font-semibold">Check</th>
                  <th className="px-4 py-2.5 font-semibold w-20">Status</th>
                  <th className="px-4 py-2.5 font-semibold">Value / Notes</th>
                  {upsell && <th className="px-4 py-2.5 font-semibold w-36">Action</th>}
                </tr>
              </thead>
              <tbody>
                {checks.map((c, i) => (
                  <tr key={i} className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors">
                    <td className="px-6 py-3 align-top">
                      <div className="font-medium">{c.label}</div>
                      {c.status !== "pass" && c.advice && (
                        <div className="text-xs text-ink-muted mt-1 flex gap-1.5">
                          <Lightbulb size={12} weight="fill" className="text-warning mt-0.5 shrink-0" />
                          <span>{c.advice}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-4 py-3 align-top mono text-xs text-ink break-all">
                      {c.value !== null && c.value !== undefined ? String(c.value) : "—"}
                    </td>
                    {upsell && (
                      <td className="px-4 py-3 align-top">
                        {c.status !== "pass" ? (
                          <a
                            href={ytEnquiryUrl({
                              siteUrl: upsell.siteUrl,
                              sectionLabel: upsell.sectionLabel,
                              issue: c.label,
                              advice: c.advice || "",
                            })}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] font-semibold border transition-colors px-2.5 py-1 rounded-sm whitespace-nowrap hover:text-white"
                            style={{ borderColor: theme.color, color: theme.color, ["--hover-bg"]: theme.color }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = theme.color; e.currentTarget.style.color = "white"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = theme.color; }}
                            data-testid={`${testId}-fix-${i}`}
                          >
                            Fix it for me
                            <ArrowSquareOut size={10} weight="bold" />
                          </a>
                        ) : (
                          <span className="text-[11px] text-ink-muted mono">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pass: { cls: "text-success border-success bg-success/5", icon: CheckCircle, label: "PASS" },
    warn: { cls: "text-warning border-warning bg-warning/5", icon: Warning, label: "WARN" },
    fail: { cls: "text-critical border-critical bg-critical/5", icon: XCircle, label: "FAIL" },
  };
  const s = map[status] || map.warn;
  const I = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 border rounded-sm mono text-[10px] font-semibold tracking-wider ${s.cls}`}>
      <I size={12} weight="fill" /> {s.label}
    </span>
  );
}

function TechStack({ tech }) {
  if (!tech?.items?.length) return null;
  const cats = tech.categories || {};
  const catKeys = Object.keys(cats);
  const maxCount = Math.max(1, ...catKeys.map((k) => cats[k]));
  const theme = SECTION_THEME.tech;
  return (
    <div className="surface-card overflow-hidden relative" data-testid="section-tech-stack">
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: theme.color }} />
      <div className="px-6 py-5 border-b border-border flex items-center gap-4" style={{ background: theme.soft }}>
        <div className="w-12 h-12 rounded-sm flex items-center justify-center shrink-0" style={{ background: theme.color }}>
          <Globe size={24} weight="fill" className="text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="font-display font-black text-xl tracking-tight" style={{ color: theme.color }}>
              {theme.label}
            </div>
            <div className="text-xs text-ink-muted">— {theme.blurb}</div>
          </div>
          <div className="text-xs text-ink-muted mono mt-1">
            {tech.count} technologies · {catKeys.length} categories
          </div>
        </div>
        <div className="num-display text-4xl font-black tabular-nums" style={{ color: theme.color }}>
          {tech.count}
        </div>
      </div>

      {/* Category distribution bars */}
      {catKeys.length > 0 && (
        <div className="px-6 py-5 border-b border-border" data-testid="tech-categories">
          <div className="label-overline mb-3">Distribution by Category</div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {catKeys.map((c) => {
              const count = cats[c];
              const pct = (count / maxCount) * 100;
              return (
                <div key={c} className="flex items-center gap-3">
                  <div className="text-xs font-semibold text-ink truncate w-32 shrink-0">{c}</div>
                  <div className="flex-1 h-2 bg-secondary rounded-sm overflow-hidden">
                    <div
                      className="h-full rounded-sm"
                      style={{ width: `${pct}%`, background: theme.color, transition: "width 0.6s ease-out" }}
                    />
                  </div>
                  <div className="text-xs mono font-bold w-6 text-right tabular-nums" style={{ color: theme.color }}>
                    {count}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Item grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
        {tech.items.map((t, i) => (
          <div key={i} className="bg-white px-6 py-4 hover:bg-secondary/40 transition-colors">
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <div className="font-semibold text-ink truncate">{t.name}</div>
              <div className="text-[10px] mono uppercase tracking-wider" style={{ color: theme.color }}>
                {t.category}
              </div>
            </div>
            <div className="text-[10px] mono text-ink-muted">{t.evidence}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AuditRunning() { return null; /* deprecated — see ScanningStage */ }
